import { motion } from "motion/react";
import styled, { css } from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greyMui,
  MOBILE_BREAKPOINT,
  orangeMui,
  purpleMui,
  redMui,
  ScreenDepth,
} from "@ryde/theme";

/** Grid columns are driven by motion style (gridTemplateColumns) for smooth resize. */
export const RideDrawerWrapper = styled(motion.div).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-drawer-wrapper",
})`
  display: grid;
  grid-template-areas:
    "header    header     header     header"
    "lhandle   form       mhandle    map"
    "lhandle   footer     mhandle    map";
  grid-template-rows: 67px 1fr 64px;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;

  .RideDrawer-Header {
    grid-area: header;
  }

  .RideDrawer-Form {
    grid-area: form;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  .RideDrawer-Footer {
    grid-area: footer;
  }

  .RideDrawer-Map {
    grid-area: map;
  }

  .RideDrawer-LeftHandle,
  .RideDrawer-MiddleHandle {
    background-color: ${greyMui._300};
    cursor: col-resize;
    user-select: none;
    z-index: ${ScreenDepth.GRID_SPLITTER};
    position: relative;

    &:hover {
      background-color: ${greyMui._400};
    }

    &:focus-visible {
      outline: 2px solid ${blueMui._500};
      outline-offset: -2px;
    }
  }

  .RideDrawer-LeftHandle {
    grid-area: lhandle;
  }

  .RideDrawer-MiddleHandle {
    grid-area: mhandle;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    flex-direction: column;

    .RideDrawer-Form {
      flex: 1;
      min-height: 0;
      height: auto;
    }

    .RideDrawer-LeftHandle,
    .RideDrawer-MiddleHandle {
      display: none;
    }
  }
`;

export const RideTabsWrapperStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-tabs-wrapper",
})`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

export const RideTabsScrollableContent = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-tabs-scrollable-content",
})`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

export const RideTabsMobileHeaderWrap = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-tabs-mobile-header-wrap",
})`
  flex-shrink: 0;
`;

export const RideTabsEarsContainer = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-tabs-ears-container",
})`
  display: flex;
  flex-shrink: 0;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-inline-start: 22px;
  padding-inline-end: 22px;
  min-height: fit-content;

  .ride-tabs-toggle-button-group-wrap {
    display: inline-flex;
    flex-shrink: 0;
  }

  .ride-tabs-toggle-button-group-wrap .MuiToggleButtonGroup-root {
    flex-wrap: nowrap;
    flex-shrink: 0;
  }

  .ride-tabs-toggle-button-group-wrap
    .MuiToggleButtonGroup-root
    .MuiToggleButton-root {
    min-width: 100px;
    width: auto;
    max-width: none;
    height: 32px;
    flex: 0 0 auto;
    white-space: nowrap;
    padding-inline: 10px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 12px;
    padding-inline-end: 12px;
  }
`;

/** New-incidents count inside ride drawer Incidents tab label (e.g. bold red “1” in “1/6”). */
export const RideIncidentsTabNewCountStyled = styled.span.attrs<IStyledAttributes>(
  {
    "data-e2e": "ride-incidents-tab-new-count",
  },
)`
  color: ${redMui.A400};
  font-weight: 700;
`;

export const RideTab1Content = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-tab1-content",
})`
  padding-inline-start: 22px;
  margin-top: 12px;
`;

export const RideTabsErrorContainer = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-tabs-errors-container",
})`
  flex-shrink: 0;
  padding-inline-end: 18px;
`;

export const RideTabsTabBasicStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-tab-basic",
})`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
  padding-inline-start: 22px;
  padding-inline-end: 22px;
  margin-top: 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 12px;
    padding-inline-end: 12px;
    width: 100%;
  }
`;

export const RideTabsTabAdvancedStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-tab-advanced",
})`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  padding-inline-start: 22px;
  padding-inline-end: 22px;
  margin-top: 12px;
  width: 85%;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: calc(100% - 30px);
    padding-inline-start: 12px;
  }
`;

export const RideBasicTabInputs = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-basic-tab-inputs",
})<{ $twoColumns?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 128px 1fr;
  column-gap: 15px;
  row-gap: 15px;
  & > div:first-child {
    order: 1;
  }
  & > div:nth-child(2) {
    order: 2;
  }

  ${({ $twoColumns }) =>
    $twoColumns &&
    css`
      grid-template-columns: 1fr 1fr;
      & > div:first-child,
      & > div:nth-child(2) {
        order: 0;
      }
    `};

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    row-gap: 15px;
    grid-template-columns: 1fr;
  }
`;

export const RideMapWrapperStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-map-wrapper",
})`
  height: 100%;
  width: 100%;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 10px;
  }
`;

export const RideMapStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-map",
})`
  height: calc(100vh - 67px);
  position: sticky;
  top: 62px;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: calc(100vh - 232px);
  }
`;

export const RideBottomBlockStyled = styled.span.attrs<IStyledAttributes>({
  "data-e2e": "ride-bottom-block",
})`
  color: ${blueGreyMui._500};
  display: inline-flex;
  font-size: 12px;
  padding-inline-end: 7px;
  gap: 4px;
  align-items: baseline;

  label {
    color: ${greyMui._500};
  }
  span {
    color: ${blueGreyMui._700};
    font-weight: 500;
  }

  /* Tooltip trigger: "⚠️ Bookings:" as one inline phrasing unit */
  label .ride-bookings-route-mismatch-label-trigger {
    display: inline;
    white-space: nowrap;
    color: ${greyMui._500};
    cursor: default;
    font-weight: 500;
  }

  label .ride-bookings-route-mismatch-warning-icon {
    margin-inline-end: 3px;
    vertical-align: text-bottom;
    line-height: 1;
  }

  label [data-e2e="framer-tooltip"] {
    display: inline !important;
    width: auto !important;
    max-width: none !important;
  }

  .booking-id.booking-id--route-passenger-mismatch {
    background-color: ${orangeMui._100};
    outline: 1px solid ${orangeMui._300};
    outline-offset: 0;
    border-radius: 2px;
    padding: 0 2px;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  .ride-bottom-site-item {
    display: inline;
    white-space: nowrap;
  }

  .ride-bottom-site-item-warning-icon {
    margin-inline-end: 3px;
    vertical-align: text-bottom;
    line-height: 1;
  }

  .ride-bottom-site-item--passengerSite.link_clickable {
    color: ${redMui._800};

    &:hover {
      color: ${redMui._900};
      text-decoration: underline;
    }
  }

  .ride-bottom-site-item--matchesRideSite.link_clickable {
    color: ${greyMui._600};
    font-weight: 300;

    &:hover {
      color: ${greyMui._800};
      text-decoration: underline;
    }
  }

  .ride-bottom-site-item--rideSiteMismatch.link_clickable {
    background-color: ${orangeMui._100};
    outline: 1px solid ${orangeMui._300};
    outline-offset: 0;
    border-radius: 2px;
    padding: 0 2px;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    color: ${orangeMui._800};
  }

  .ride-bottom-site-item--noSite {
    color: ${greyMui._600};
    font-weight: 500;
  }

  .link_clickable {
    cursor: pointer;
    &:hover {
      color: ${blueGreyMui._900};
      text-decoration: underline;
    }
  }

  &:not(&:last-child) {
    &:after {
      content: "";
      border-inline-end: 1px solid ${blueGreyMui._500};
      height: 8px;
      margin-inline-start: 4px;
      display: inline-block;
    }
  }

  &.duplication-source-ride {
    label {
      display: flex;
      align-items: center;
      cursor: pointer;
    }
  }

  &.return-of-ride {
    label {
      display: flex;
      align-items: center;
      cursor: pointer;
    }
  }

  .ride-header-related-hub-link {
    margin-inline-start: 4px;
    color: ${blueGreyMui._700};
    text-decoration: none;
    cursor: pointer;
    &:hover {
      color: ${blueGreyMui._900};
      text-decoration: none;
    }
  }
  .ride-header-related-hub-id {
    margin-inline-start: 4px;
    color: ${blueGreyMui._700};
  }

  &.ride-detour-indicator {
    align-items: center;

    .ride-indicators__detour {
      font-size: 16px;
      vertical-align: text-bottom;
      color: ${purpleMui._600};
      cursor: default;
    }
  }

  &.ride-had-detour-indicator {
    align-items: center;

    .ride-indicators__had-detour {
      font-size: 16px;
      vertical-align: text-bottom;
      color: ${greyMui._500};
      cursor: default;
    }
  }

  .booking-id {
    font-weight: 400;
    font-size: 11px;

    &.status-canceled {
      color: ${greyMui._500};
    }

    &.status-routed {
      color: ${purpleMui._600};
    }

    &.status-new {
      color: ${blueMui._600};
    }

    &.status-rejected {
      color: ${redMui._600};
    }

    &.status-onhold {
      color: ${orangeMui._600};
    }

    &.status-draft {
      color: ${greyMui._400};
    }
  }
`;

export const RideEditTopInfoContainerStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "ride-edit-top-info-container",
  },
)`
  flex-shrink: 0;

  .info_item {
    padding-inline-start: 22px;
    padding-inline-end: 25px;
    padding-top: 5px;
    padding-bottom: 5px;
    &:first-child {
      padding-top: 10px;
    }
    p {
      .info_icon {
        margin-inline-end: 10px;
      }

      font-size: 14px;
      font-weight: 500;
      padding: 5px;
      color: ${orangeMui._800};
      background-color: ${orangeMui._50};
      outline: 1px solid ${orangeMui._100};
      border-radius: 4px;
      display: flex;
      align-items: center;

      .display-near-by-link {
        margin-inline-start: 4px;
        text-decoration: underline;
        display: inline-block;
        font-weight: 700;
      }
    }
  }
`;

export const RideSelectDirectionStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-select-direction",
})`
  .ride-select-direction {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .ride-select-direction-icon {
    width: 80px;
    height: 90px;
  }

  .ride-select-direction-text {
    margin-top: 8px;
    font-weight: bold;
    font-size: 16px;
    color: #4a209c;
  }

  .ride-select-direction-text-highlighted {
    color: #30cd89;
  }
`;
